
import React, { useEffect } from 'react';
import { Plus, User, FileText, HelpCircle } from 'lucide-react';
import { AnimatedSidebarLink } from "@/components/ui/animated-sidebar";
import { useAuth } from '@/context/AuthContext';
import { SafeIcon } from '@/components/ui/safe-icon';

export interface SidebarLink {
  label: string;
  href: string;
  icon: React.ReactNode;
  onClick: (e?: React.MouseEvent) => void;
}

interface SidebarLinksProps {
  links: SidebarLink[];
}

const SidebarLinks: React.FC<SidebarLinksProps> = ({ links }) => {
  return (
    <div className="space-y-3">
      {links.map((link, idx) => (
        <AnimatedSidebarLink key={idx} link={link} />
      ))}
    </div>
  );
};

export const createDefaultLinks = (
  onNewChat: () => void, 
  onAccountClick: () => void,
  onLogout: () => void,
  isAdmin: boolean
): SidebarLink[] => {
  // Debug log for admin status when creating links
  console.log("[SidebarLinks] Creating links with isAdmin:", isAdmin);
  
  // Create base links
  const defaultLinks = [
    {
      label: "Neue Konversation",
      href: "#",
      icon: (
        <Plus className="text-gray-300 h-5 w-5 flex-shrink-0" />
      ),
      onClick: (e?: React.MouseEvent) => {
        if (e) e.preventDefault(); // Prevent default navigation
        console.log("New chat button clicked");
        onNewChat();
      }
    },
    {
      label: "Konto",
      href: "/account",
      icon: (
        <User className="text-gray-300 h-5 w-5 flex-shrink-0" />
      ),
      onClick: onAccountClick
    },
    {
      label: "Dokumentation",
      href: "/documentation",
      icon: (
        <FileText className="text-gray-300 h-5 w-5 flex-shrink-0" />
      ),
      onClick: () => window.location.href = "/documentation"
    },
    {
      label: "Support",
      href: "/account?tab=support",
      icon: (
        <HelpCircle className="text-gray-300 h-5 w-5 flex-shrink-0" />
      ),
      onClick: () => window.location.href = "/account?tab=support"
    },
    {
      label: "Abmelden",
      href: "#",
      icon: (
        <SafeIcon name="LogOut" className="text-gray-300 h-5 w-5 flex-shrink-0" />
      ),
      onClick: onLogout
    },
  ];
  
  // Add admin link if user is admin - placed BEFORE the logout link
  if (isAdmin) {
    const adminLink = {
      label: "Admin Dashboard",
      href: "/beta-admin",
      icon: (
        <SafeIcon name="ShieldCheck" className="text-jade h-5 w-5 flex-shrink-0" />
      ),
      onClick: () => window.location.href = "/beta-admin"
    };
    
    // Insert admin link before the logout link
    defaultLinks.splice(defaultLinks.length - 1, 0, adminLink);
    
    console.log("[SidebarLinks] Admin link added:", adminLink);
  }
  
  return defaultLinks;
};

export default SidebarLinks;
